import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsModal from "../clients/Tools/kit/ToolsModal";
import PlaybookScriptService from "../../services/PlaybookScriptService";
import UtilityService from "../../services/UtilityService";

const MODAL_WIDTH = 640;
const NO_APPROVER_NAME = "someone no longer listed";
const TAKE_LEAD = "Take one onto this step and it arrives with the firm's approval.";
const BROWSE_LEAD = "Scripts this firm has approved, newest first.";
const KEPT_SCRIPT_LINE =
	"This step still holds its earlier script, so the firm's script replaces its code and keeps its name, and only a script of the same kind can be taken.";
const RETIRE_WARNING = "A retired script can't be taken again. Steps that already use it keep it.";

class PlaybookScriptLibraryModal extends WComponent {
	state = {
		nameFor: {},
		unfoldedNameId: null,
		confirmingRetireId: null,
		refusalFor: {},
	};

	onTake = async ({ firmScriptId }) => {
		const { sessionId, gapId, firmScripts, holdsAKeptScript, runScriptAction, onClose } = this.props;
		const { nameFor, refusalFor } = this.state;
		const entry = firmScripts.find(candidate => candidate.id === firmScriptId);
		const hasTypedName = nameFor[firmScriptId] !== undefined;
		const chosenName = hasTypedName ? nameFor[firmScriptId] : entry.name;
		const trimmedName = chosenName.trim();
		const isNameGiven = !holdsAKeptScript && trimmedName !== "";
		const scriptKey = isNameGiven ? trimmedName : null;
		await this.update({ refusalFor: { ...refusalFor, [firmScriptId]: null }, confirmingRetireId: null });

		const { refusal } = await runScriptAction({
			busyKey: `takeScript-${firmScriptId}`,
			action: () => PlaybookScriptService.takeScriptFromLibrary({ sessionId, gapId, firmScriptId, scriptKey }),
			gapId,
		});
		if (!refusal) {
			onClose();
			return;
		}
		const unfoldedNameId = holdsAKeptScript ? null : firmScriptId;
		await this.update(previous => ({ refusalFor: { ...previous.refusalFor, [firmScriptId]: refusal }, unfoldedNameId }));
	};

	onRetire = async ({ firmScriptId }) => {
		const { gapId, runScriptAction } = this.props;
		const { refusalFor } = this.state;
		await this.update({ refusalFor: { ...refusalFor, [firmScriptId]: null } });

		const { refusal } = await runScriptAction({
			busyKey: `retireScript-${firmScriptId}`,
			action: () => PlaybookScriptService.retireLibraryScript({ firmScriptId }),
			gapId,
		});
		await this.update(previous => ({ refusalFor: { ...previous.refusalFor, [firmScriptId]: refusal || null }, confirmingRetireId: null }));
	};

	render() {
		const { isOpen, gapId, firmScripts = [], viewer, busyKey, isConversationOpen, holdsAKeptScript, onClose } = this.props;
		const { nameFor, unfoldedNameId, confirmingRetireId, refusalFor } = this.state;
		const hasGap = gapId !== null && gapId !== undefined;
		const canTake = hasGap && Boolean(isConversationOpen);
		const canChooseName = canTake && !holdsAKeptScript;
		const isKeptScriptLineShown = canTake && Boolean(holdsAKeptScript);
		const canRetire = Boolean(viewer && viewer.canApprove);
		const isBusy = Boolean(busyKey);
		const hasEntries = firmScripts.length > 0;

		return (
			<ToolsModal open={isOpen} onClose={onClose} title="The firm's scripts" width={MODAL_WIDTH}>
				{hasEntries && <p className="tk-lead tk-lead--muted">{canTake ? TAKE_LEAD : BROWSE_LEAD}</p>}
				{hasEntries && isKeptScriptLineShown && <div className="tk-notice">{KEPT_SCRIPT_LINE}</div>}
				{!hasEntries && <div className="tk-empty">The firm has no scripts yet.</div>}
				{hasEntries && (
					<ul className="playbook-script-library__list">
						{firmScripts.map(entry => {
							const firmScriptId = entry.id;
							const approverName = entry.approvedByName || NO_APPROVER_NAME;
							const approvedOn = UtilityService.formatDate({ value: entry.approvedAt });
							const hasReadTools = entry.readTools.length > 0;
							const isNameUnfolded = unfoldedNameId === firmScriptId;
							const hasTypedName = nameFor[firmScriptId] !== undefined;
							const nameValue = hasTypedName ? nameFor[firmScriptId] : entry.name;
							const isTaking = busyKey === `takeScript-${firmScriptId}`;
							const isRetiring = busyKey === `retireScript-${firmScriptId}`;
							const isConfirmingRetire = confirmingRetireId === firmScriptId;
							const refusal = refusalFor[firmScriptId];

							return (
								<li key={firmScriptId} className="playbook-script-library__entry">
									<div className="playbook-script-library__name">{entry.name}</div>
									<p className="playbook-script-library__description">{entry.plainDescription}</p>
									{hasReadTools && (
										<div className="tk-chips">
											<span className="tk-chips__label">Reads</span>
											{entry.readTools.map(toolName => (
												<span key={toolName} className="tk-chip">
													{toolName}
												</span>
											))}
										</div>
									)}
									<div className="playbook-script-library__approved">
										<Icon.Shield size={13} />
										Approved by {approverName} on {approvedOn}
									</div>

									{isNameUnfolded && canChooseName && (
										<div className="tk-field playbook-script-library__name-field">
											<label className="tk-field-label" htmlFor={`script-library-name-${firmScriptId}`}>
												Its name on this playbook
											</label>
											<input
												id={`script-library-name-${firmScriptId}`}
												className="tk-input"
												value={nameValue}
												onChange={event => this.update({ nameFor: { ...nameFor, [firmScriptId]: event.target.value } })}
												disabled={isBusy}
											/>
											<span className="tk-muted">Left empty, it keeps the name "{entry.name}".</span>
										</div>
									)}

									{isConfirmingRetire && (
										<div className="tk-notice tk-notice--danger playbook-script-library__confirm">
											<div className="tk-notice__title">
												<Icon.AlertTriangle size={14} />
												{RETIRE_WARNING}
											</div>
											<div className="playbook-script-library__actions">
												<button type="button" className="tk-button tk-button--danger tk-button--compact" onClick={() => this.onRetire({ firmScriptId })} disabled={isBusy}>
													{isRetiring ? "Retiring…" : "Retire — this can't be undone"}
												</button>
												<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.update({ confirmingRetireId: null })} disabled={isBusy}>
													Keep it
												</button>
											</div>
										</div>
									)}

									{!isConfirmingRetire && (canTake || canRetire) && (
										<div className="playbook-script-library__actions">
											{canTake && (
												<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onTake({ firmScriptId })} disabled={isBusy}>
													{isTaking ? "Taking…" : "Take onto this step"}
												</button>
											)}
											{canChooseName && !isNameUnfolded && (
												<button type="button" className="tk-text-action tk-text-action--muted" onClick={() => this.update({ unfoldedNameId: firmScriptId })} disabled={isBusy}>
													Choose its name
												</button>
											)}
											{canRetire && (
												<button
													type="button"
													className="tk-text-action playbook-script-library__retire"
													onClick={() => this.update({ confirmingRetireId: firmScriptId, refusalFor: { ...refusalFor, [firmScriptId]: null } })}
													disabled={isBusy}
												>
													Retire
												</button>
											)}
										</div>
									)}

									{refusal && (
										<div className="tk-notice tk-notice--danger playbook-script-library__refusal">
											<div className="tk-notice__title">
												<Icon.AlertTriangle size={14} />
												{refusal}
											</div>
										</div>
									)}
								</li>
							);
						})}
					</ul>
				)}
			</ToolsModal>
		);
	}
}

export default PlaybookScriptLibraryModal;
